import { useEffect, useState } from 'react';
import type { HeldOrderRow } from '@matix/types';
import { useParkedOrdersStore, type ParkedOrderRow } from '../../store/parkedOrders';
import { useHeldOrders } from '../../store/heldOrders';
import { useReceiptsStore } from '../../store/receipts';
import { toast } from '../../store/toast';
import { askManagerPassword } from '../../store/managerPrompt';
import { useAuth } from '@matix/auth';
import { VoidReceiptDialog } from '../receipts/VoidReceiptDialog';
import { formatTimeAgo } from './helpers';
import { useCurrency } from '../print/useCurrency';
import { useLang } from '../../lib/language';

/**
 * The Held Orders screen.
 *
 * HELD INVOICES are real receipts with `Inv_Receipt_h.IsHolded = 1` — what
 * the Hold button writes, and what the legacy till's "Holded Orders" screen
 * listed. Each card offers what the legacy screen did:
 *   - Pay     open it and take the money (it keeps its number and date)
 *   - Open    load it into the cart to add or remove items, then Hold again
 *             or Charge
 *   - Print   print it as it stands, unpaid
 *   - Cancel  void it (the Cancel Order permission, an optional reason)
 *
 * HELD ON THIS DEVICE is the older local hold — a cart snapshot, not an
 * invoice. Still used for what an invoice cannot carry (delivery, membership,
 * returns, an offline till), and for anything held before this change. Shown
 * below, and only when there is any.
 */
export interface ParkedOrdersScreenProps {
  /** Recall a LOCAL (snapshot) hold into the cart. */
  onRecall: (id: string) => Promise<void>;
  /** Load a held invoice into the cart; `pay` opens the payment modal too. */
  onOpenHeld: (id: number, pay: boolean) => Promise<void>;
  /** Print a held invoice as it stands. */
  onPrintHeld: (id: number) => void;
}

export function ParkedOrdersScreen(props: ParkedOrdersScreenProps) {
  const { t } = useLang();
  const localRows = useParkedOrdersStore((s) => s.rows);
  const refreshLocal = useParkedOrdersStore((s) => s.refresh);
  const removeLocal = useParkedOrdersStore((s) => s.remove);

  const held = useHeldOrders((s) => s.rows);
  const heldLoading = useHeldOrders((s) => s.loading);
  const heldError = useHeldOrders((s) => s.error);
  const scope = useHeldOrders((s) => s.scope);
  const canViewBranch = useHeldOrders((s) => s.canViewBranch);
  const setScope = useHeldOrders((s) => s.setScope);
  const refreshHeld = useHeldOrders((s) => s.refresh);
  const cancelHeld = useHeldOrders((s) => s.cancel);

  const me = useAuth().cashier?.sub ?? null;
  const cancelReasons = useReceiptsStore((s) => s.cancelReasons);
  const loadCancelReasons = useReceiptsStore((s) => s.loadCancelReasons);
  const [cancelTarget, setCancelTarget] = useState<HeldOrderRow | null>(null);
  /** The manager's password, collected before the reason dialog opens. */
  const [cancelPassword, setCancelPassword] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // Refresh on mount — another till may have held, paid or cancelled one.
  useEffect(() => {
    void refreshHeld();
    void refreshLocal();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function confirmCancel(reasonId: number | null, note: string | null): Promise<void> {
    if (!cancelTarget) return;
    setCancelling(true);
    if (!cancelPassword) return;
    const r = await cancelHeld(cancelTarget.id, cancelPassword, reasonId, note);
    setCancelling(false);
    toast(r.message, r.ok ? 'success' : 'error');
    if (r.ok) {
      setCancelTarget(null);
      setCancelPassword(null);
    }
  }

  const nothing = held.length === 0 && localRows.length === 0 && !heldLoading;

  return (
    <div className="parked-screen" style={screenStyle}>
      <div style={headerStyle}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
          {t({ en: 'Held orders', ar: 'الطلبات المعلقة' })}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {canViewBranch && (
            <div role="tablist" style={{ display: 'flex', gap: 4 }}>
              {(['mine', 'branch'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  role="tab"
                  aria-selected={scope === s}
                  onClick={() => setScope(s)}
                  style={scope === s ? scopeOnStyle : scopeOffStyle}
                >
                  {s === 'mine'
                    ? t({ en: 'Mine', ar: 'طلباتي' })
                    : t({ en: 'Whole branch', ar: 'كل الفرع' })}
                </button>
              ))}
            </div>
          )}
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {held.length + localRows.length}{' '}
            {held.length + localRows.length === 1 ? t({ en: 'order', ar: 'طلب' }) : t({ en: 'orders', ar: 'طلبات' })}
            {heldLoading && t({ en: ' · loading…', ar: ' · جارٍ التحميل…' })}
          </span>
        </div>
      </div>

      {heldError && (
        <div role="alert" style={{ color: 'var(--danger, #dc2626)', fontSize: 13 }}>
          {heldError}
        </div>
      )}

      {nothing ? (
        <EmptyState />
      ) : (
        <>
          {held.length > 0 && (
            <div style={gridStyle}>
              {held.map((row) => (
                <HeldInvoiceCard
                  key={row.id}
                  row={row}
                  showCashier={scope === 'branch'}
                  me={me}
                  onPay={() => void props.onOpenHeld(row.id, true)}
                  onOpen={() => void props.onOpenHeld(row.id, false)}
                  onPrint={() => props.onPrintHeld(row.id)}
                  onCancel={() => {
                    // Cancelling a held order needs the MANAGER's password
                    // (LoginName 'Admin'); the reason is asked after it.
                    void (async () => {
                      const pw = await askManagerPassword({
                        en: `Cancel held order #${row.entry_no}`,
                        ar: `إلغاء الطلب المعلق #${row.entry_no}`,
                      });
                      if (!pw) return;
                      void loadCancelReasons();
                      setCancelPassword(pw);
                      setCancelTarget(row);
                    })();
                  }}
                />
              ))}
            </div>
          )}

          {localRows.length > 0 && (
            <>
              <h3 style={{ margin: '8px 0 0', fontSize: 14, fontWeight: 600, color: 'var(--text-muted)' }}>
                {t({ en: 'Held on this device', ar: 'معلقة على هذا الجهاز' })}
              </h3>
              <div style={gridStyle}>
                {localRows.map((row) => (
                  <LocalHoldCard
                    key={row.id}
                    row={row}
                    onRecall={() => props.onRecall(row.id)}
                    onDelete={() => {
                      if (window.confirm(t({ en: 'Delete this held order? This cannot be undone.', ar: 'هل تريد حذف هذا الطلب المعلق؟ لا يمكن التراجع عن هذا الإجراء.' }))) {
                        void removeLocal(row.id);
                      }
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      <VoidReceiptDialog
        row={cancelTarget}
        busy={cancelling}
        reasons={cancelReasons}
        onCancel={() => {
          setCancelTarget(null);
          setCancelPassword(null);
        }}
        onConfirm={(reasonId, note) => void confirmCancel(reasonId, note)}
      />
    </div>
  );
}

function useMoney(): (n: number) => string {
  const currency = useCurrency();
  const digits = Math.max(0, Math.min(8, currency.precision));
  return (n: number) => `${(Number.isFinite(n) ? n : 0).toFixed(digits)} ${currency.code}`;
}

function HeldInvoiceCard(props: {
  row: HeldOrderRow;
  showCashier: boolean;
  me: number | null;
  onPay: () => void;
  onOpen: () => void;
  onPrint: () => void;
  onCancel: () => void;
}) {
  const { t, lang } = useLang();
  const { row } = props;
  const fmtMoney = useMoney();
  const customer =
    (lang === 'ar' ? row.customer_name_ar || row.customer_name : row.customer_name || row.customer_name_ar) ||
    t({ en: 'Walk-in', ar: 'عميل عابر' });
  const table =
    row.table_number != null && String(row.table_number).trim() !== ''
      ? `${t({ en: 'Table', ar: 'طاولة' })} ${row.table_number}`
      : null;

  return (
    <div style={cardStyle} className="held-card">
      <div style={cardHeaderStyle}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle} title={customer}>
            #{row.entry_no} · {customer}
          </div>
          {(table || row.notes) && (
            <div style={subStyle} title={[table, row.notes].filter(Boolean).join(' · ')}>
              {[table, row.notes].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      </div>

      <div style={metaStyle}>
        <span>
          {row.item_count} {row.item_count === 1 ? t({ en: 'item', ar: 'صنف' }) : t({ en: 'items', ar: 'أصناف' })}
        </span>
        <span> · </span>
        <strong>{fmtMoney(row.net_value)}</strong>
        <span> · </span>
        <span style={{ color: 'var(--text-muted)' }}>{formatTimeAgo(row.entry_date)}</span>
        {props.showCashier && row.employee_name && (
          <>
            <span> · </span>
            <span style={{ color: 'var(--text-muted)' }}>{row.employee_name}</span>
          </>
        )}
      </div>

      {row.open_by && row.open_by.user_id !== props.me && (
        <div
          style={{
            alignSelf: 'flex-start',
            padding: '3px 10px',
            borderRadius: 999,
            background: '#BB3838',
            color: '#FFFFFF',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {t({
            en: `Open by ${row.open_by.user_name ?? '—'} now`,
            ar: `مفتوح الآن لدى ${row.open_by.user_name ?? '—'}`,
          })}
        </div>
      )}

      <div style={cardFooterStyle}>
        <button type="button" onClick={props.onPay} style={primaryBtnStyle}>
          {t({ en: 'Pay', ar: 'دفع' })}
        </button>
        <button type="button" onClick={props.onOpen} style={secondaryBtnStyle}>
          {t({ en: 'Open', ar: 'فتح' })}
        </button>
        <button type="button" onClick={props.onPrint} style={secondaryBtnStyle}>
          {t({ en: 'Print', ar: 'طباعة' })}
        </button>
        {/* Cancelling voids the invoice and frees its table (a table is
            held only while its order is). It needs the MANAGER's password,
            asked when pressed — not a permission of the cashier's own. */}
        <button
          type="button"
          onClick={props.onCancel}
          style={cancelBtnStyle}
          title={t({
            en: "Cancel this order and free its table — needs the manager's password",
            ar: 'إلغاء هذا الطلب وتحرير طاولته — يتطلب كلمة مرور المدير',
          })}
        >
          {t({ en: 'Cancel', ar: 'إلغاء' })}
        </button>
      </div>
    </div>
  );
}

function LocalHoldCard(props: {
  row: ParkedOrderRow;
  onRecall: () => void;
  onDelete: () => void;
}) {
  const { t } = useLang();
  const { row } = props;
  const fmtMoney = useMoney();
  const customerLabel = row.customer_label || t({ en: 'Walk-in', ar: 'عميل عابر' });

  return (
    <div style={cardStyle} className="parked-card">
      <div style={cardHeaderStyle}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle} title={customerLabel}>
            {customerLabel}
          </div>
          {row.note && (
            <div style={subStyle} title={row.note}>
              {row.note}
            </div>
          )}
        </div>
        <StatusDot status={row.status} />
      </div>

      <div style={metaStyle}>
        <span>
          {row.item_count} {row.item_count === 1 ? t({ en: 'item', ar: 'صنف' }) : t({ en: 'items', ar: 'أصناف' })}
        </span>
        <span> · </span>
        <strong>{fmtMoney(row.total_value)}</strong>
        <span> · </span>
        <span style={{ color: 'var(--text-muted)' }}>{formatTimeAgo(row.parked_at)}</span>
      </div>

      <div style={cardFooterStyle}>
        <button type="button" onClick={props.onRecall} style={primaryBtnStyle} className="parked-recall-btn">
          {t({ en: 'Recall', ar: 'استرجاع' })}
        </button>
        <button
          type="button"
          onClick={props.onDelete}
          style={deleteBtnStyle}
          className="parked-delete-btn"
          aria-label={t({ en: 'Delete held order', ar: 'حذف الطلب المعلق' })}
          title={t({ en: 'Delete', ar: 'حذف' })}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function StatusDot(props: { status: 'synced' | 'pending' | 'failed' }) {
  const { t } = useLang();
  const { status } = props;
  const color =
    status === 'synced' ? 'var(--success, #16a34a)' :
    status === 'pending' ? 'var(--warning, #d97706)' :
    'var(--danger, #dc2626)';
  const title =
    status === 'synced' ? t({ en: 'Synced', ar: 'تمت المزامنة' }) :
    status === 'pending' ? t({ en: 'Saving in background', ar: 'يتم الحفظ في الخلفية' }) :
    t({ en: 'Failed to sync — will retry', ar: 'فشلت المزامنة — ستتم إعادة المحاولة' });
  return (
    <span
      title={title}
      style={{ width: 8, height: 8, borderRadius: 4, background: color, flex: '0 0 auto', marginTop: 6 }}
    />
  );
}

function EmptyState() {
  const { t } = useLang();
  return (
    <div style={emptyStyle}>
      <div style={{ fontSize: 48, opacity: 0.3 }}>📋</div>
      <div style={{ fontSize: 16, fontWeight: 600, marginTop: 12 }}>{t({ en: 'No held orders', ar: 'لا توجد طلبات معلقة' })}</div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--text-muted)',
          marginTop: 6,
          maxWidth: 360,
          textAlign: 'center',
          lineHeight: 1.5,
        }}
      >
        {t({
          en: <>Add items to the cart and tap <strong>Hold</strong> to save an order you can pay later.</>,
          ar: <>أضف أصنافاً إلى السلة واضغط <strong>تعليق</strong> لحفظ طلب يمكنك دفعه لاحقاً.</>,
        })}
      </div>
    </div>
  );
}

// ── Inline styles (no CSS import needed) ─────────────────────────────────

const screenStyle: import('react').CSSProperties = {
  padding: '20px 24px 32px',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  flex: 1,
  overflowY: 'auto',
};

const headerStyle: import('react').CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 12,
};

const gridStyle: import('react').CSSProperties = {
  // min(260px, 100%) keeps a card from ever exceeding the container width,
  // so the grid drops to a single column on narrow phones instead of
  // overflowing horizontally.
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
  gap: 14,
};

const cardStyle: import('react').CSSProperties = {
  background: 'var(--surface, #fff)',
  border: '1px solid var(--border, #e2e6ec)',
  borderRadius: 12,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
};

const cardHeaderStyle: import('react').CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
};

const titleStyle: import('react').CSSProperties = {
  fontWeight: 700,
  fontSize: 16,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const subStyle: import('react').CSSProperties = {
  fontSize: 12,
  color: 'var(--text-muted)',
  marginTop: 4,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const metaStyle: import('react').CSSProperties = {
  fontSize: 13,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 4,
};

const cardFooterStyle: import('react').CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
};

const primaryBtnStyle: import('react').CSSProperties = {
  flex: 1,
  padding: '10px 14px',
  borderRadius: 8,
  border: 'none',
  background: 'var(--primary, #2563eb)',
  color: '#fff',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
};

const secondaryBtnStyle: import('react').CSSProperties = {
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid var(--border, #e2e6ec)',
  background: 'var(--surface, #fff)',
  color: 'var(--text, inherit)',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
};

const cancelBtnStyle: import('react').CSSProperties = {
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid var(--danger, #dc2626)',
  background: 'var(--surface, #fff)',
  color: 'var(--danger, #dc2626)',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
};

const deleteBtnStyle: import('react').CSSProperties = {
  width: 40,
  padding: '10px 0',
  borderRadius: 8,
  border: '1px solid var(--border, #e2e6ec)',
  background: 'var(--surface, #fff)',
  color: 'var(--danger, #dc2626)',
  fontSize: 16,
  cursor: 'pointer',
};

const scopeOnStyle: import('react').CSSProperties = {
  padding: '6px 12px',
  borderRadius: 999,
  border: '1px solid var(--primary, #2563eb)',
  background: 'var(--primary, #2563eb)',
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const scopeOffStyle: import('react').CSSProperties = {
  ...scopeOnStyle,
  background: 'var(--surface, #fff)',
  color: 'var(--text, inherit)',
  border: '1px solid var(--border, #e2e6ec)',
};

const emptyStyle: import('react').CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 40,
};
